import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import {
  fetchTicket,
  replyTicket,
  setTicketStatus,
  type AdminTicketDetail,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { RelativeTime } from '@/components/DataDisplay'
import { StatusBadge } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'

/**
 * A7.07/08 — Tiket kartasi + yozishmalar tarixi. GET /admin/tickets/:id, POST
 * /admin/tickets/:id/reply, POST /admin/tickets/:id/status (backend `650928d`).
 */
export function TicketCard() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const { id = '' } = useParams()
  const [ticket, setTicket] = useState<AdminTicketDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [reply, setReply] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchTicket(Number(id), ac.signal)
      .then((tk) => {
        if (alive) setTicket(tk)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const sendReply = async () => {
    if (!reply.trim()) return
    setBusy(true)
    try {
      await replyTicket(Number(id), reply.trim())
      setReply('')
      toast(t.support.replySent, { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast(t.support.actionFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  const changeStatus = async (status: string) => {
    setBusy(true)
    try {
      await setTicketStatus(Number(id), status)
      toast(t.support.statusChanged, { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast(t.support.actionFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title={ticket ? `#${ticket.id} · ${ticket.subject}` : t.support.card}
        screenId="A7.07"
        actions={
          <Link
            to="/support"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.support.queue}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-2xl space-y-2">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !ticket ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-2xl space-y-3">
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border-default bg-surface-2 p-3">
              <StatusBadge status={ticket.status === 'open' ? 'open' : ticket.status === 'pending' ? 'pending' : 'deleted'} />
              <span className="text-sm text-tertiary">{ticket.category}</span>
              <Link to={`/users/${ticket.user_id}`} className="text-sm text-accent hover:underline">
                {ticket.username ? `@${ticket.username}` : `#${ticket.user_id}`}
              </Link>
              <div className="ml-auto flex gap-1.5">
                {(['open', 'pending', 'closed'] as const).map((st) => (
                  <button
                    key={st}
                    disabled={busy || ticket.status === st}
                    onClick={() => changeStatus(st)}
                    className={cn(
                      'rounded-md border px-2 py-1 text-xs',
                      ticket.status === st
                        ? 'border-accent bg-accent-subtle text-accent'
                        : 'border-border-default bg-surface-1 text-secondary hover:text-primary',
                      'disabled:opacity-60',
                    )}
                  >
                    {t.support[st]}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {ticket.replies.map((r, i) => (
                <div
                  key={i}
                  className={cn(
                    'max-w-[85%] rounded-lg border p-3',
                    r.is_staff
                      ? 'ml-auto border-accent/40 bg-accent-subtle'
                      : 'border-border-default bg-surface-2',
                  )}
                >
                  <div className="mb-1 flex items-center gap-2 text-xs text-tertiary">
                    <span>{r.is_staff ? t.support.staff : t.support.user}</span>
                    <RelativeTime value={r.created_at} />
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-primary">{r.body}</div>
                </div>
              ))}
            </div>

            {ticket.status !== 'closed' && (
              <div className="rounded-lg border border-border-default bg-surface-2 p-3">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={t.support.replyPlaceholder}
                  rows={3}
                  className="mb-2 w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
                />
                <button
                  disabled={busy || !reply.trim()}
                  onClick={sendReply}
                  className="rounded-md border border-accent bg-accent-subtle px-3 py-1.5 text-sm text-accent hover:bg-accent hover:text-white disabled:opacity-50"
                >
                  {t.support.send}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
